import { Component, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormArray, Validators, AbstractControl } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store, select } from '@ngrx/store';
import { Observable, Subject, combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, map, pluck, switchMap, withLatestFrom, take, share } from 'rxjs/operators';

import { LanguageService } from '@fcom/ui-translate';
import { NotificationLayout } from '@fcom/ui-components';
import { capitalize } from '@fcom/core/utils';
import { ToasterService } from '@fcom/common';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { Profile } from '@fcom/core-api/login';

import { Passengers, Passenger, Buyer, VoucherType } from '../../api/models';
import { PaymentTotal, State, StatusState, Stage, FlightBasket, PaymentType } from '../../interfaces';
import {
  getBasketPassengerIds,
  getPassengers,
  selectStatusState,
  getBasketFlights,
  getMaxPassengers,
  getMinimumPaymentOptions,
  getUser,
  getChildIsTravellingAlone,
} from '../../store/selectors';
import { getVoucherAmount } from '../../utils';
import { BasketActions, StatusActions } from '../../store/actions';

export const TOASTER_MAX_PAX_ID = 'UPGRADE_MAX_PAX_SELECTED';
export const TOASTER_CHILD_TRAVELLING_ALONE_ID = 'CHILD_TRAVELLING_ALONE';

@Component({
  selector: 'fin-upgrade-passenger-selection',
  templateUrl: 'passenger-selection.component.html',
  styleUrls: ['passenger-selection.component.scss'],
})
export class PassengerSelectionComponent implements OnInit, OnDestroy {
  readonly NotificationLayout = NotificationLayout;
  readonly Stage = Stage;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  subscriptions: Subscription = new Subscription();
  passengerForm: UntypedFormGroup;
  passengers$: Observable<Passenger[]>;
  selectedPassengers$: Observable<string[]>;
  selectedFlights$: Observable<FlightBasket[]>;
  maxPassengers$: Observable<number>;
  currentStage$: Observable<StatusState>;
  minPaymentOptions$: Observable<PaymentTotal>;
  loggedInProfile$: Observable<Profile>;
  user$: Observable<Buyer>;
  paxStream$: Observable<{ passengers: Passengers; maxPax: number; selected: string[]; atPaymentLimit: boolean }>;
  childIsTravellingAlone$: Observable<boolean>;
  showPaxLimit$: Observable<boolean>;

  onPassengerToggle$ = new Subject<{ selected: boolean; passengerId: string }>();

  constructor(
    private store: Store<State>,
    private fb: UntypedFormBuilder,
    private language: LanguageService,
    private toaster: ToasterService
  ) {}

  ngOnInit(): void {
    this.passengers$ = this.store.pipe(select(getPassengers));
    this.selectedPassengers$ = this.store.pipe(select(getBasketPassengerIds));
    this.selectedFlights$ = this.store.pipe(select(getBasketFlights));
    this.maxPassengers$ = this.store.pipe(select(getMaxPassengers));
    this.currentStage$ = this.store.pipe(select(selectStatusState));
    this.minPaymentOptions$ = this.store.pipe(select(getMinimumPaymentOptions));
    this.loggedInProfile$ = this.store.pipe(profileOrUndefinedIfNotLoggedIn());
    this.user$ = this.store.pipe(select(getUser));
    this.childIsTravellingAlone$ = this.store.pipe(select(getChildIsTravellingAlone));
    this.paxStream$ = combineLatest([this.passengers$, this.maxPassengers$, this.selectedPassengers$]).pipe(
      switchMap(([passengers, maxPax, selected]) => {
        return this.atPaymentLimit(selected.length).pipe(
          map((atPaymentLimit) => ({ passengers, maxPax, selected, atPaymentLimit }))
        );
      }),
      share()
    );

    this.showPaxLimit$ = combineLatest([this.passengers$, this.maxPassengers$]).pipe(
      map(([pax, max]) => max < pax.length)
    );

    this.subscriptions.add(
      this.paxStream$.subscribe(({ passengers, maxPax, selected, atPaymentLimit }) => {
        this.constructForm(passengers, maxPax, selected, atPaymentLimit);
      })
    );

    this.subscriptions.add(
      this.showMaxPaxToaster().subscribe((showToaster: boolean) => {
        this.toggleToaster(showToaster, TOASTER_MAX_PAX_ID, 'oneUpgrade.toaster.maxPaxSelected');
      })
    );

    this.subscriptions.add(
      this.childIsTravellingAlone$
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe((childIsTravellingAlone: boolean) => {
          this.toggleToaster(
            childIsTravellingAlone,
            TOASTER_CHILD_TRAVELLING_ALONE_ID,
            'oneUpgrade.toaster.childCannotTravelAlone'
          );
        })
    );

    this.subscriptions.add(
      this.onPassengerToggle$.pipe(withLatestFrom(this.selectedFlights$)).subscribe(([data, selectedFlights]) => {
        this.togglePassenger(data.selected, data.passengerId);

        if (selectedFlights.length) {
          this.store.dispatch(BasketActions.clearFlights());
        }
      })
    );

    this.subscriptions.add(
      combineLatest([this.passengers$, this.currentStage$])
        .pipe(take(1))
        .subscribe(([passengers, { stage }]) => {
          if (stage === Stage.PASSENGERS && passengers.length < 2) {
            this.store.dispatch(StatusActions.setStage({ stage: Stage.FLIGHTS }));
          }
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get seatInfoText(): Observable<string> {
    return this.maxPassengers$.pipe(
      map<number, [string, number]>((max) => [
        max === 1 ? 'oneUpgrade.upgradeSelection.seatLimit.singular' : 'oneUpgrade.upgradeSelection.seatLimit.plural',
        max,
      ]),
      switchMap(([key, max]): Observable<string> => this.language.translate(key, { amount: max }))
    );
  }

  get passengerControls(): AbstractControl[] {
    return (this.passengerForm.get('passengers') as UntypedFormArray).controls;
  }

  showMaxPaxToaster(): Observable<boolean> {
    return this.paxStream$.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(({ passengers, selected, atPaymentLimit }) => Boolean(atPaymentLimit && selected.length < passengers.length))
    );
  }

  getPassengerDetails(index: number): Observable<Passenger> {
    return this.passengers$.pipe(pluck(index));
  }

  constructForm(passengers: Passenger[], maxPax: number, selectedPax: string[] = [], paymentLimit = false): void {
    this.passengerForm = this.fb.group({
      hidden: this.fb.control(''), // this prevents whole form from being in disabled state when selected passenger is disabled
      passengers: this.fb.array(
        passengers.map((pax) => {
          if (passengers.length === 1 && !selectedPax.includes(pax.passengerId)) {
            this.store.dispatch(BasketActions.addPaxToBasket({ passengerId: pax.passengerId }));
            this.store.dispatch(StatusActions.setStage({ stage: Stage.FLIGHTS }));
          }

          const disabled = (selectedPax.length >= maxPax || paymentLimit) && !selectedPax.includes(pax.passengerId);

          return this.fb.control({
            value: selectedPax.includes(pax.passengerId) || passengers.length === 1,
            disabled: passengers.length === 1 || disabled,
          });
        }),
        [Validators.required]
      ),
    });
  }

  togglePassenger(selected: boolean, passengerId: string): void {
    if (selected) {
      this.store.dispatch(BasketActions.addPaxToBasket({ passengerId }));
    } else {
      this.store.dispatch(BasketActions.removePassenger({ passengerId }));
    }
  }

  onChangePassengers(): void {
    this.store.dispatch(StatusActions.setStage({ stage: Stage.PASSENGERS }));
  }

  getSelectedName(passengerId: string): Observable<string> {
    return this.passengers$.pipe(
      map(
        (passengers) =>
          passengers
            .filter((pax) => pax.passengerId === passengerId)
            .map((pax) => `${capitalize(pax.firstName)} ${capitalize(pax.lastName)}`)[0] || ''
      )
    );
  }

  /**
   * If only points or voucher payment available check if user has sufficient points or vouchers to upgrade an additional passenger
   * @param pax current number of selected passengers
   */
  private atPaymentLimit(pax: number): Observable<boolean> {
    return combineLatest([this.loggedInProfile$, this.user$, this.minPaymentOptions$]).pipe(
      take(1),
      map(([loggedIn, buyer, payment]) => {
        if (!loggedIn || payment.money) {
          return false;
        } // not at limit if money option or not loggedIn

        if (
          (!payment.points || (buyer.points || 0) < payment.points * (pax + 1)) && // no points payment or insufficient points
          (!payment[PaymentType.EUROPE_UPGRADE] ||
            getVoucherAmount(VoucherType.EuropeUpgrade, buyer) < payment[PaymentType.EUROPE_UPGRADE] * (pax + 1)) && // no shortHaul payment or insufficient vouchers
          (!payment[PaymentType.LONG_HAUL_UPGRADE] ||
            getVoucherAmount(VoucherType.LongHaulUpgrade, buyer) <
              payment[PaymentType.LONG_HAUL_UPGRADE] * (pax + 1)) && // no longHaul payment or insufficient vouchers
          (!payment[PaymentType.UPGRADE_BENEFIT] ||
            getVoucherAmount(VoucherType.UpgradeBenefit, buyer) < payment[PaymentType.UPGRADE_BENEFIT] * (pax + 1)) // no upgrade_benefit payment or insufficient vouchers
        ) {
          return true;
        }
        return false;
      })
    );
  }

  /**
   * Shows or hides the toaster message
   * @param showToaster boolean value to show or hide the message
   * @param id id of the toaster in question
   * @param contentKey translation key of the message
   */
  private toggleToaster(showToaster: boolean, id: string, contentKey: string): void {
    if (showToaster) {
      this.toaster.addMessageToQueue({
        id,
        contentKey,
        autoClose: false,
        beneathModal: true,
      });
    } else {
      this.toaster.removeMessage(id);
    }
  }
}
